package top.wcpe.allincore.ui.runtime.ir.prop

import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.event.MouseButtonNames
import top.wcpe.allincore.ui.runtime.ir.DslRuntimeError
import kotlin.jvm.JvmStatic

/**
 * RFC-INPUT-EVENT-UNIFICATION Stage 3：通用 hotkey 属性贡献者。
 *
 * 把原 Button 专属的 `hotkey` prop 提升为所有 [UIElement] 通用，
 * 并在写入时校验主键名：
 * - 禁止 `"Left"` / `"Right"` / `"Middle"`（与
 *   `onClick` / `onRightClick` / `onMiddleClick` 入口语义重叠）
 * - 允许键盘键名（`"Enter"` / `"Ctrl+S"` 等）与鼠标侧键
 *   （`"MouseBack"` / `"Ctrl+MouseForward"` 等）
 *
 * 校验失败抛 [DslRuntimeError]，错误信息提示开发者改用专用入口。
 */
internal object HotkeyPropContributor {

    @JvmStatic
    fun contribute(m: MutableMap<String, PropBinding>) {
        m.put(
            "hotkey",
            PropBinding.writeOnly { el, v ->
                val hotkey = v?.toString()
                // null / 空串：清除绑定，无需校验
                if (hotkey.isNullOrEmpty()) {
                    el.hotkey(null)
                    return@writeOnly
                }
                val main = extractMainKey(hotkey)
                rejectMouseMainButton(main)
                el.hotkey(hotkey)
            },
        )
    }

    /**
     * 去掉 `Ctrl+` / `Shift+` / `Alt+` 等修饰键前缀，返回纯主键名。
     * 不做大小写归一化（仅在比较时按大小写不敏感处理）。
     */
    private fun extractMainKey(hotkey: String): String {
        val idx = hotkey.lastIndexOf('+')
        return if (idx < 0) hotkey else hotkey.substring(idx + 1)
    }

    /**
     * 主键若落在鼠标主键集合中，抛错。提示信息引导开发者改用专用点击入口。
     */
    private fun rejectMouseMainButton(main: String) {
        if (main.isEmpty()) return
        val lower = main.lowercase()
        if (lower == MouseButtonNames.LEFT.lowercase() ||
            lower == MouseButtonNames.RIGHT.lowercase() ||
            lower == MouseButtonNames.MIDDLE.lowercase()
        ) {
            throw DslRuntimeError(
                "hotkey 不接受鼠标主键 '$main'，请改用 onClick / onRightClick / onMiddleClick",
            )
        }
    }
}
